import { Component, inject, Input, signal } from '@angular/core';
import { BasicForm } from '@shared/components/classes/basic.form';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputValidatorDirective } from '@common/directive/input-validator.directive';
import { PasswordModule } from '@primeng/password';
import { InputNumberModule } from '@primeng/inputnumber';
import { CalendarModule } from '@primeng/calendar';
import { DropdownModule } from '@primeng/dropdown';
import { MultiSelectModule } from '@primeng/multiselect';
import { CheckboxModule } from '@primeng/checkbox';
import { RadioButtonModule } from '@primeng/radiobutton';
import { SliderModule } from '@primeng/slider';
import { InputSwitchModule } from '@primeng/inputswitch';
import { InputTextareaModule } from '@primeng/inputtextarea';
import { ColorPickerModule } from '@primeng/colorpicker';
import { ChipsModule } from '@primeng/chips';
import { InputMaskModule } from '@primeng/inputmask';
import { FloatLabelModule } from '@primeng/floatlabel';
import { ToggleButtonModule } from "@primeng/togglebutton";
import { MetersService } from '@modules/settings/meters/meters.service';
import { AutocompleteComponent } from '@shared/components/autocomplete/autocomplete.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NgLabelTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { PropertiesService } from '@modules/settings/properties/properties.service';
import { CategoryType, ConnectStatus } from '@modules/settings/meters/meter.enum';
import { Observable } from 'rxjs';
import { NgIf } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { UnitTypeDirective } from '@common/directive/unit-type.directive';
import { UnitTypesService } from '@modules/settings/unit-types/unit-types.service';
import { ApartmentsService } from '@modules/universal-billing/apartments/apartments.service';
import { DatepickerComponent } from '@shared/components/datepicker/datepicker.component';
import { Property } from '@modules/universal-billing/properties/properties.interface';
import { Apartment } from '@modules/universal-billing/apartments/apartments.interface';
import { InputLabelComponent } from '@shared/components/input-label/input-label.component';
import {
  InputValidatorComponent
} from '@common/components/input-validator-component/input-validator-component/input-validator';

@Component({
  selector: 'app-meter-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputValidatorDirective,
    PasswordModule,
    InputNumberModule,
    CalendarModule,
    DropdownModule,
    MultiSelectModule,
    CheckboxModule,
    RadioButtonModule,
    SliderModule,
    InputSwitchModule,
    InputTextareaModule,
    ColorPickerModule,
    ChipsModule,
    InputMaskModule,
    FloatLabelModule,
    ToggleButtonModule,
    AutocompleteComponent,
    TranslatePipe,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgIf,
    NgxMaskDirective,
    UnitTypeDirective,
    DatepickerComponent,
    InputLabelComponent,
    InputValidatorComponent
  ],
  templateUrl: './meter-form.component.html',
  styleUrl: './meter-form.component.scss',
})
export class MeterFormComponent extends BasicForm<any> {
  @Input() building: Property;
  @Input() apartment: Apartment;

  _defaultService: MetersService = inject(MetersService);
  unitTypeService = inject(UnitTypesService)
  propertiesService = inject(PropertiesService);
  apartmentsService = inject(ApartmentsService);
  category: CategoryType;

  form: FormGroup;
  searchLoading = signal(false);
  dateFields = ['installationDate'];
  categoryType = [
    { id: CategoryType.MAIN, label: this.translateService.instant('METER.MAIN') },
    { id: CategoryType.APARTMENT, label: this.translateService.instant('METER.APARTMENT_METER') }
  ];

  constructor(private fb: FormBuilder) {
    super();
    this.form = this.fb.group({
      name: [null],
      unitTypeId: [null],
      category: [null, Validators.required],
      installationReading: [null, Validators.required],
      buildingId: [null, Validators.required],
      apartmentId: [null],
      apartment: [null],
      installationLocation: [null, Validators.required],
      installationDate: [null, Validators.required],
      manufacturer: [null, Validators.required],
      model: [null, Validators.required],
      serialNumber: [null, Validators.required],
      quantity: [null, Validators.required],
      isSmartMeter: [true, Validators.required],
      connectivityStatus: [ConnectStatus.ONLINE, Validators.required],
      readingUrl: [''],
      description: [null],
      unit: [null]
    });
  }

  ngOnInit() {
    super.ngOnInit();
    this.category = this.apartment ? CategoryType.APARTMENT : (this.building ? CategoryType.MAIN : null);

    this.form.get('category').valueChanges.subscribe(type => {
      this.form.get('apartmentId').setValidators(type === CategoryType.APARTMENT ? [Validators.required] : []);
    })

    this.form.patchValue({
      buildingId: this.building?.id,
      apartmentId: this.apartment?.id,
      apartment: this.apartment?.apartmentName,
      category: this.category
    })
  }
  onSearch() {
    const { serialNumber } = this.form.value;
    if (serialNumber) {
      this.searchLoading.set(true);
      this._defaultService.meterSearchRemote(serialNumber)
        .subscribe({
          next: data => {
            this.form.patchValue({
              name: serialNumber,
              installationReading: 0,
              quantity: 9,
              readingUrl: 'izarplus',
              installationDate: data.device?.installDate,
              manufacturer: data.device?.manufacturer,
              model: data.device?.devicekey,
              installationLocation: data.device?.location?.id,
            })
            this.searchLoading.set(false);
          },
          error: err => {
            this.sweetAlertService.warn('', 'METER_SEARCH_ERROR_MESSAGE',  this.translateService.instant('METER_SEARCH_ERROR_TITLE'))
            this.searchLoading.set(false);
          }
        })
    }
  }
  patchResponseData(res: any) {
    if (this.category) {
      res.category = this.category;
      res.buildingId = this.building?.id;
      res.apartmentId = this.apartment?.id;
    } else {
      res.buildingId = res.buildingId ?? res.apartment?.buildingId;
    }
    res.unit = res.unitType.type;
    super.patchResponseData(res);
  }

  submitAction(data): Observable<any> {
    if (data.category === CategoryType.APARTMENT) {
      return super.submitAction(data);
    } else if (data.category === CategoryType.MAIN) {
      return this.isEdit ? this._defaultService.updateToBuilding(data, this.selectedRow.id) : this._defaultService.createToBuilding(data);
    }
  }
  setUnit(unit) {
    this.form.patchValue({
      unit: unit.type
    })
  }
  protected readonly CategoryType = CategoryType;
}
